import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, Download, TrendingUp, DollarSign } from 'lucide-react';
import financeService from '../../services/financeService';

const FinanceAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await financeService.getFinanceAnalytics(dateRange.start, dateRange.end);
      setAnalytics(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleExportCSV = () => {
    if (!analytics) return;

    const csvData = [
      ['Financial Report', ''],
      ['Period', `${dateRange.start} to ${dateRange.end}`],
      ['Generated', new Date().toLocaleString()],
      [''],
      ['Metric', 'Value'],
      ['Total Revenue', analytics.summary.total_revenue],
      ['Pending Payments', analytics.summary.total_pending],
      ['Total Transactions', analytics.summary.total_transactions],
      ['Avg per Transaction', analytics.summary.avg_per_transaction],
      ['Collection Rate', analytics.summary.collection_rate + '%'],
      [''],
      ['Payment Mode', 'Amount'],
      ['Cash', analytics.payment_mode_distribution.cash],
      ['UPI', analytics.payment_mode_distribution.upi],
      ['Card', analytics.payment_mode_distribution.card],
      ['Insurance', analytics.payment_mode_distribution.insurance],
      [''],
      ['Month', 'Revenue', 'Sessions'],
      ...analytics.monthly_revenue.map(m => [m.month + ' ' + m.year, m.revenue, m.sessions])
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
        <button onClick={fetchAnalytics} className="ml-4 text-sm underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  // Prepare chart data
  const paymentModeData = analytics ? [
    { name: 'Cash', value: analytics.payment_mode_distribution.cash || 0, color: '#10b981' },
    { name: 'UPI', value: analytics.payment_mode_distribution.upi || 0, color: '#3b82f6' },
    { name: 'Card', value: analytics.payment_mode_distribution.card || 0, color: '#8b5cf6' },
    { name: 'Insurance', value: analytics.payment_mode_distribution.insurance || 0, color: '#f59e0b' }
  ] : [];

  const monthlyRevenueData = analytics?.monthly_revenue || [];

  return (
    <div className="space-y-6">
      {/* Header with Date Range and Export */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Financial Analytics</h2>
            <p className="text-gray-600 mt-1">Revenue trends and payment insights</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <button
              onClick={handleExportCSV}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Revenue</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(analytics?.summary?.total_revenue || 0)}</p>
            </div>
            <DollarSign className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Transactions</p>
              <p className="text-3xl font-bold mt-2">{analytics?.summary?.total_transactions || 0}</p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Avg per Session</p>
              <p className="text-3xl font-bold mt-2">
                {formatCurrency(analytics?.summary?.avg_per_transaction || 0)}
              </p>
            </div>
            <DollarSign className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Pending</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(analytics?.summary?.total_pending || 0)}</p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-80" />
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Line Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Mode Distribution Pie Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Mode Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={paymentModeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {paymentModeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {paymentModeData.map((mode, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: mode.color }}></div>
                  <span className="text-sm text-gray-700">{mode.name}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{formatCurrency(mode.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sessions vs Revenue</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyRevenueData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="revenue" fill="#3b82f6" name="Revenue (Rupees)" />
            <Bar yAxisId="right" dataKey="sessions" fill="#10b981" name="Sessions" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Key Metrics Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Collection Rate</p>
            <div className="flex items-end">
              <p className="text-2xl font-bold text-gray-900">
                {analytics?.summary?.collection_rate?.toFixed(1) || 0}%
              </p>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${analytics?.summary?.collection_rate || 0}%` }}
              ></div>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
            <p className="text-2xl font-bold text-gray-900">{analytics?.summary?.total_transactions || 0}</p>
            <p className="text-xs text-gray-500 mt-1">In selected period</p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Cash vs Digital</p>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.payment_mode_distribution ? 
                (() => {
                  const cash = analytics.payment_mode_distribution.cash || 0;
                  const digital = (analytics.payment_mode_distribution.upi || 0) + 
                                 (analytics.payment_mode_distribution.card || 0);
                  const total = cash + digital;
                  return total > 0 ? 
                    `${((cash/total)*100).toFixed(0)}% : ${((digital/total)*100).toFixed(0)}%` : 
                    'N/A';
                })()
                : 'N/A'
              }
            </p>
            <p className="text-xs text-gray-500 mt-1">Cash : Digital ratio</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceAnalytics;